export const ENVMAPPING = (
  location: string
): {
  api: string;
  agentApi: string;
  azure_app_id: string;
  azure_client_id: string;
  track_request_url: string;
  origin?: string;
  portalSwitchUrl?: string;
} => {
  const localData = {
    azure_app_id: "ba96876a-c846-4b5f-9278-cbf1df45996e",
    azure_client_id: "9107b728-2166-4e5d-8d13-d1ffdf0351ef",
    track_request_url: "https://tmnatest.service-now.com",
  };

  if (location === "https://enclave.datascience.toyota.com") {
    return {
      api: "https://api.datascience.toyota.com/enc/",
      agentApi: "https://api.datascience.toyota.com/enc/",
      azure_app_id: "cff840a9-3f08-4681-b1b6-627238b2bf0e",
      azure_client_id: "8c642d1d-d709-47b0-ab10-080af10798fb",
      track_request_url: "https://tmna.service-now.com",
      origin: "https://enclave.datascience.toyota.com",
      portalSwitchUrl: "https://datascience.toyota.com",
    };
  } else if (location === "https://enclave-nonprod.datascience.toyota.com") {
    return {
      api: "https://api.nonprod.datascience.toyota.com/enc/",
      agentApi: "https://api.nonprod.datascience.toyota.com/enc/",
      azure_app_id: "ba96876a-c846-4b5f-9278-cbf1df45996e",
      azure_client_id: "9107b728-2166-4e5d-8d13-d1ffdf0351ef",
      track_request_url: "https://tmnatest.service-now.com",
      origin: "https://enclave-nonprod.datascience.toyota.com",
      portalSwitchUrl: "https://nonprod.datascience.toyota.com",
    };
  }

  return {
    ...localData,
    api: "http://localhost:8080/api/enc/",
    agentApi: "http://localhost:8080/api/enc/",
    origin: "http://localhost:3000",
    portalSwitchUrl: "http://localhost:3001",
  };
};

export const toBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const dropdownSelectProps = {
  MenuProps: {
    PaperProps: {
      style: {
        maxHeight: 200,
      },
    },
  },
};

export const buildPageTree = <T extends { page_id: string; parent_page_id?: string | null; position?: number }>(
  pages: T[]
): (T & { children?: T[] })[] => {
  const nodeMap = new Map<string, T & { children?: T[] }>();
  pages.forEach((page) => nodeMap.set(page.page_id, { ...page, children: [] }));

  const roots: (T & { children?: T[] })[] = [];
  nodeMap.forEach((node) => {
    if (node.parent_page_id && nodeMap.has(node.parent_page_id)) {
      nodeMap.get(node.parent_page_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortByPosition = (nodes: (T & { children?: T[] })[]) => {
    nodes.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    nodes.forEach((node) => {
      if (node.children?.length) sortByPosition(node.children);
      else delete node.children;
    });
  };
  sortByPosition(roots);

  return roots;
};

/**
 * Determine whether the signed-in user is an admin by decoding the Azure AD
 * access token and inspecting its `roles` claim.
 */
export const isRoleAdmin = (accessToken: string): boolean => {
  if (!accessToken) { return false; }
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1])) || {};
    const roles: unknown[] = Array.isArray(payload.roles) ? payload.roles : [];
    return roles.some((role: unknown) => typeof role === 'string' && role.toLowerCase().includes('admin'));
  } catch {
    return false;
  }
};

export const getFilteredRoles = (roles: any[]) => {
  if (!roles || !Array.isArray(roles)) { return []; }
  return roles.filter(role => {
    const roleName = role.u_primary?.toLowerCase().trim();
    return roleName !== 'data lead' && roleName !== 'data analyst';
  });
};

export const truncateWords = (
  text: string,
  maxWords = 35
): { text: string; truncated: boolean } => {
  if (!text) { return { text: '', truncated: false }; }
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) { return { text, truncated: false }; }
  return { text: `${words.slice(0, maxWords).join(' ')}...`, truncated: true };
};

export const formatRelativeTime = (isoDate: string): string => {
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) { return ''; }
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) { return 'just now'; }
  const units: [number, string][] = [
    [60, 'minute'],
    [60, 'hour'],
    [24, 'day'],
    [30, 'month'],
    [12, 'year'],
  ];
  let value = seconds;
  let label = 'second';
  for (const [factor, name] of units) {
    if (value < factor) { break; }
    value = Math.floor(value / factor);
    label = name;
  }
  return `${value} ${label}${value === 1 ? '' : 's'} ago`;
};